import React, { useEffect } from 'react';
import styled from 'styled-components';
import { DialogContent, DialogActions, Button, Box, IconButton, Stack, Switch, FormControlLabel, Typography } from '@mui/material';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { NgDialog } from '../NgDialog/NgDialog';
import {
  useGetPresentationThemeByIdQuery,
  useUpdatePresentationThemeMutation,
  useUpdatePresentationThemeLayoutMutation,
} from '../../services/presentationThemes.service';
import { SlideItem } from '../FolderManager/Components/SlideItem';
import { BlockInputsSettings } from '../Workspaces/Pages/Templates/components/MicrositeTemplateEditor/components/PageTemplateEditor/components/BlockInputsSettings';
import { NgPanelHeader, SkeletonLoader } from '../UI';
import { useItemThumbnail } from '../../hooks';
import { mergeSchemas } from '../../helpers';

const SlidesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(245px, 1fr));
  gap: 20px;
  padding: 20px;
  width: 100%;
`;

const SchemaConfiguratorWrapper = styled.div`
  & > div {
    min-height: unset;
    padding: 0;
    background: transparent;
  }
`;

const TwoColumnLayout = styled.div`
  display: flex;
  height: 100%;
`;

const LayoutsPanel = styled.div`
  width: 285px;
  flex-shrink: 0;
  border-right: 1px solid ${({ theme }) => theme.palette.border || '#e0e0e0'};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
  padding: ${({ theme }) => theme.spacing.small};
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.small};
`;

const SettingsPanel = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const ThemeThumbnailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 245px;
  border-radius: ${({ theme }) => theme.radius.default};
`;

const ThemeThumbnailImage = styled.img`
  width: 245px;
  height: 138px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.default};
`;

const ThemeThumbnailPlaceholder = styled.div`
  width: 245px;
  height: 138px;
  border-radius: ${({ theme }) => theme.radius.default};
  background-color: ${({ theme }) => theme.palette.grey || '#f5f5f5'};
`;

interface ManageThemeDialogProps {
  open: boolean;
  onClose: () => void;
  themeData: FolderManagerItem;
  onSave: (updatedTheme: any) => void;
}

const UNION_KEY = 'union' as const;

export const ManageThemeDialog: React.FC<ManageThemeDialogProps> = ({ open, onClose, themeData }) => {
  const [updatePresentationTheme] = useUpdatePresentationThemeMutation();
  const [updatePresentationThemeLayout, { isLoading: isTogglingVisibility }] =
    useUpdatePresentationThemeLayoutMutation();

  const presentationThemeId = themeData.presentationTheme?.ID;

  const { data: presentationTheme, isLoading } = useGetPresentationThemeByIdQuery(
    { themeId: presentationThemeId as number },
    { skip: !presentationThemeId },
  );

  const [activeView, setActiveView] = React.useState<'grid' | 'settings'>('grid');
  const [selectedIndex, setSelectedIndex] = React.useState<number | typeof UNION_KEY | null>(null);
  const layoutItemRefs = React.useRef<Record<number, HTMLDivElement | null>>({});
  // Local overrides keyed by layout index — seeded from API on load, updated on save.
  const [layoutSchemas, setLayoutSchemas] = React.useState<Record<number, any>>({});

  const { thumbnailUrl: themeThumbnailUrl } = useItemThumbnail({
    itemId: themeData?.id,
    itemType: themeData?.type,
  });

  const selectedLayout =
    typeof selectedIndex === 'number'
      ? presentationTheme?.Layouts?.find((l) => l.index === selectedIndex) ?? null
      : null;

  const getLayoutSchema = (layout: PresentationThemeLayout) =>
    layoutSchemas[layout.index] ?? layout.values_schema ?? null;

  // merge all layout schemas into one — deduplicate by key (first occurrence wins).
  const unionSchema = React.useMemo(
    () => mergeSchemas((presentationTheme?.Layouts ?? []).map(getLayoutSchema)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layoutSchemas, presentationTheme],
  );

  // keys appearing in more than one layout belong to global config, not per-layout editors.
  const sharedKeys = React.useMemo(() => {
    const layouts = presentationTheme?.Layouts ?? [];
    if (layouts.length <= 1) return new Set<string>();
    const keyCounts: Record<string, number> = {};
    layouts.forEach((layout) => {
      Object.entries(getLayoutSchema(layout)?.properties ?? {}).forEach(([key, def]: [string, any]) => {
        if (def?.inputType !== 'hidden') keyCounts[key] = (keyCounts[key] ?? 0) + 1;
      });
    });
    return new Set(
      Object.entries(keyCounts)
        .filter(([, c]) => c > 1)
        .map(([k]) => k),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutSchemas, presentationTheme]);

  const getLayoutSpecificSchema = (layout: PresentationThemeLayout) => {
    const schema = getLayoutSchema(layout);
    if (!schema) return null;
    const filteredProps = Object.fromEntries(Object.entries(schema.properties).filter(([key]) => !sharedKeys.has(key)));
    return { ...schema, properties: filteredProps };
  };

  const hasVisibleFields = (layout: PresentationThemeLayout) => {
    const schema = getLayoutSpecificSchema(layout);
    return Object.values(schema?.properties ?? {}).some((p: any) => p?.inputType !== 'hidden');
  };

  const hasMismatchWithUnion = (layout: PresentationThemeLayout) => {
    const schema = getLayoutSchema(layout);
    return Object.entries(schema?.properties ?? {}).some(([key, def]) => {
      if (sharedKeys.has(key)) return false;
      const unionDef = unionSchema.properties[key];
      return unionDef && JSON.stringify(def) !== JSON.stringify(unionDef);
    });
  };

  const patchLayouts = async (updatedSchemas: Record<number, any>) => {
    if (!presentationThemeId) return;
    const layouts = (presentationTheme?.Layouts ?? []).map((l) => ({
      name: l.name,
      index: l.index,
      values_schema: updatedSchemas[l.index] ?? l.values_schema ?? { type: 'object', properties: {} },
    }));
    await updatePresentationTheme({ themeId: presentationThemeId, data: { layouts } });
  };

  const handleLayoutSchemaSave = async (schema: any) => {
    if (!selectedLayout) return;
    // merge saved unique-key schema back with the shared keys so they aren't lost in the API.
    const current = getLayoutSchema(selectedLayout);
    const sharedProps = Object.fromEntries(
      Object.entries(current?.properties ?? {}).filter(([key]) => sharedKeys.has(key)),
    );
    const merged = { ...schema, properties: { ...schema.properties, ...sharedProps } };
    const updatedSchemas = { ...layoutSchemas, [selectedLayout.index]: merged };
    setLayoutSchemas(updatedSchemas);
    await patchLayouts(updatedSchemas);
  };

  const handleUnionSchemaSave = async (schema: any) => {
    // only propagate keys whose config actually changed in the union.
    // this preserves layout-specific overrides for unchanged keys.
    const changedKeys = Object.keys(schema.properties).filter(
      (key) => JSON.stringify(schema.properties[key]) !== JSON.stringify(unionSchema.properties[key]),
    );

    if (changedKeys.length === 0) return;

    const updatedSchemas = { ...layoutSchemas };
    (presentationTheme?.Layouts ?? []).forEach((layout) => {
      const current = getLayoutSchema(layout);
      if (!current?.properties) return;
      const updatedProps = Object.fromEntries(
        Object.entries(current.properties).map(([key, def]) =>
          changedKeys.includes(key) && key in schema.properties ? [key, schema.properties[key]] : [key, def],
        ),
      );
      updatedSchemas[layout.index] = { ...current, properties: updatedProps };
    });
    setLayoutSchemas(updatedSchemas);
    await patchLayouts(updatedSchemas);
  };

  const handleToggleVisibility = async (visible: boolean) => {
    if (!presentationThemeId || !selectedLayout?.id) return;
    await updatePresentationThemeLayout({
      themeId: presentationThemeId,
      layoutId: selectedLayout.id,
      hidden: !visible,
    });
  };

  useEffect(() => {
    if (typeof selectedIndex === 'number') {
      layoutItemRefs.current[selectedIndex]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  // seed local schema state from API whenever the theme loads.
  // mormalize properties that have no inputType by inferring a sensible default
  // so the BlockInputsSettings editor doesn't fall back to "email" (its first option).
  useEffect(() => {
    if (!presentationTheme?.Layouts) return;

    const seeded: Record<number, any> = {};

    presentationTheme.Layouts.forEach((layout) => {
      if (!layout.values_schema) return;

      const normalizedProperties = Object.fromEntries(
        Object.entries(layout.values_schema.properties).map(([key, def]: [string, any]) => {
          if (def.inputType) return [key, def];
          const inputType = def.type === 'number' ? 'number' : 'text';
          return [key, { ...def, inputType }];
        }),
      );

      seeded[layout.index] = { ...layout.values_schema, properties: normalizedProperties };
    });

    setLayoutSchemas(seeded);
  }, [presentationTheme]);

  const viewSwitcher = (
    <Stack direction="row" spacing={0.5} sx={{ mr: 1 }}>
      <IconButton
        onClick={() => setActiveView('grid')}
        size="small"
        sx={{ color: activeView === 'grid' ? 'black' : 'grey.500' }}
        title="Grid view"
      >
        <GridViewOutlinedIcon sx={{ width: 20, height: 20 }} />
      </IconButton>
      <IconButton
        onClick={() => {
          setActiveView('settings');
          setSelectedIndex((prev) => prev ?? UNION_KEY);
        }}
        size="small"
        sx={{ color: activeView === 'settings' ? 'black' : 'grey.500' }}
        title="Settings"
      >
        <SettingsOutlinedIcon sx={{ width: 20, height: 20 }} />
      </IconButton>
    </Stack>
  );

  return (
    <NgDialog opened={open} title="Manage Theme" onDismiss={onClose} actions={viewSwitcher}>
      <DialogContent sx={{ p: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', flex: 1 }}>
        {isLoading ? (
          <SlidesGrid>
            <SkeletonLoader count={4} height={138} width={245} horizontal single />
          </SlidesGrid>
        ) : activeView === 'grid' ? (
          <SlidesGrid>
            {presentationTheme?.Layouts?.map((layout) => (
              <SlideItem
                key={layout.index}
                slideNumber={layout.index}
                slideTitle={layout.name}
                thumbnailUrl={layout.thumbnailLocation}
                hasSchema={hasVisibleFields(layout)}
                schemaWarning={hasMismatchWithUnion(layout)}
                onClick={
                  hasVisibleFields(layout)
                    ? () => {
                        setSelectedIndex(layout.index);
                        setActiveView('settings');
                      }
                    : undefined
                }
              />
            ))}
          </SlidesGrid>
        ) : (
          <TwoColumnLayout>
            <LayoutsPanel>
              <NgPanelHeader>Theme</NgPanelHeader>

              <ThemeThumbnailWrapper
                onClick={() => setSelectedIndex(UNION_KEY)}
                style={{
                  cursor: 'pointer',
                  outline: selectedIndex === UNION_KEY ? '2px solid #1976d2' : 'none',
                  borderRadius: 8,
                }}
              >
                {themeThumbnailUrl ? (
                  <ThemeThumbnailImage
                    src={themeThumbnailUrl}
                    alt={themeData?.displayName || themeData?.name || 'Theme'}
                  />
                ) : (
                  <ThemeThumbnailPlaceholder />
                )}
              </ThemeThumbnailWrapper>

              <NgPanelHeader>
                Layouts {presentationTheme?.Layouts?.length ? `(${presentationTheme.Layouts.length})` : ''}
              </NgPanelHeader>

              <Container>
                {presentationTheme?.Layouts?.map((layout) => (
                  <div
                    key={layout.index}
                    ref={(el) => {
                      layoutItemRefs.current[layout.index] = el;
                    }}
                  >
                    <SlideItem
                      slideNumber={layout.index}
                      slideTitle={layout.name}
                      thumbnailUrl={layout.thumbnailLocation}
                      isSelected={selectedIndex === layout.index}
                      hasSchema={hasVisibleFields(layout)}
                      schemaWarning={hasMismatchWithUnion(layout)}
                      onClick={() => setSelectedIndex(layout.index)}
                    />
                  </div>
                ))}
              </Container>
            </LayoutsPanel>

            <SettingsPanel>
              {selectedIndex === UNION_KEY ? (
                <SchemaConfiguratorWrapper>
                  <BlockInputsSettings
                    key={UNION_KEY}
                    entrypoint={{ name: 'Theme', html: '' }}
                    blocks={[]}
                    schema={{
                      ...unionSchema,
                      properties: Object.fromEntries(
                        Object.entries(unionSchema.properties).filter(([key]) => sharedKeys.has(key)),
                      ),
                    }}
                    onSave={handleUnionSchemaSave}
                    folderPickerZIndex={100000}
                    labels={{
                      title: 'Theme-wide Values Configuration',
                      subtitle: 'Configure shared input types across all layouts',
                      configurationSectionTitle: 'Theme Configuration',
                    }}
                  />
                </SchemaConfiguratorWrapper>
              ) : selectedLayout ? (
                <Box>
                  <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!selectedLayout.hidden}
                          disabled={!selectedLayout.id || isTogglingVisibility}
                          onChange={(e) => handleToggleVisibility(e.target.checked)}
                        />
                      }
                      label={selectedLayout.hidden ? 'Hidden from presentations' : 'Shown in presentations'}
                    />
                    <Typography variant="body2" color="text.secondary">
                      When hidden, this layout won&apos;t appear as an option in the presentation editor&apos;s
                      Layouts tab.
                    </Typography>
                  </Box>
                  {(() => {
                    const layoutSpecificSchema = getLayoutSpecificSchema(selectedLayout);
                    const hasSpecificFields = Object.values(layoutSpecificSchema?.properties ?? {}).some(
                      (p: any) => p?.inputType !== 'hidden',
                    );
                    return hasSpecificFields ? (
                      <SchemaConfiguratorWrapper>
                        <BlockInputsSettings
                          key={selectedLayout.index}
                          entrypoint={{ name: selectedLayout.name, html: '' }}
                          blocks={[]}
                          schema={layoutSpecificSchema}
                          onSave={handleLayoutSchemaSave}
                          folderPickerZIndex={100000}
                          fieldWarnings={Object.fromEntries(
                            Object.entries(layoutSpecificSchema?.properties ?? {})
                              .filter(([key, def]) => {
                                const unionDef = unionSchema.properties[key];
                                return unionDef && JSON.stringify(def) !== JSON.stringify(unionDef);
                              })
                              .map(([key]) => [key, 'Differs from theme-wide configuration']),
                          )}
                          labels={{
                            title: `${selectedLayout.name} Layout Values Configuration`,
                            subtitle: `Configure input types and options for "${selectedLayout.name}" layout`,
                            configurationSectionTitle: 'Layout Configuration',
                          }}
                        />
                      </SchemaConfiguratorWrapper>
                    ) : (
                      <p>All variables for this layout are managed at the theme level.</p>
                    );
                  })()}
                </Box>
              ) : (
                <p>Select the theme thumbnail or a layout to configure its settings.</p>
              )}
            </SettingsPanel>
          </TwoColumnLayout>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {/* <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button> */}
      </DialogActions>
    </NgDialog>
  );
};
